import React, { forwardRef, useEffect, useState } from 'react'
import ReactPlayer, { ReactPlayerProps } from 'react-player/lazy'

import { InsertPhoto, Warning } from '@mui/icons-material'
import { Button } from '../button/button'
import { Text } from '../text/text'
import { Paper, Stack } from '@mui/material'
import { color } from '../../theme'

interface BlobProps extends Blob {
  name: string
  lastModified: number
}

export interface MediaPlayerProps extends ReactPlayerProps {
  showCustomControls?: boolean
  thumbnail?: string
  containerStyle?: React.CSSProperties
  light?: boolean | string
  onError?: any
  onProgress?: any
  defaultProgress?: any
  autoplay?: boolean
  variant?: 'video' | 'audio'
  playerContainerStyleOverride?: React.CSSProperties
  onScreenshot?: (file: File) => void
}
export const MediaPlayer = forwardRef((props: MediaPlayerProps, ref: any) => {
  const {
    autoplay,
    playerContainerStyleOverride,
    audioContainerStyleOverride,
    defaultProgress,
    onProgress,
    onError,
    light,
    thumbnail,
    containerStyle,
    variant,
    url,
    showCustomControls,
    onScreenshot,
  } = props

  const [progress, setProgress] = useState(
    defaultProgress
      ? defaultProgress
      : {
          loaded: 0,
          loadedSeconds: 0,
          played: 0,
          playedSeconds: 0,
        },
  )

  const [volume, setVolume] = useState<undefined | number>()
  const [pos, setPos] = useState(0)
  const [muted, setMuted] = useState<boolean>(false)
  const [playing, setPlaying] = useState<boolean>(variant === 'video' && autoplay ? true : false)
  const [duration, setDuration] = useState(0)
  const [ready, setReady] = useState(false)
  const [buffering, setBuffering] = useState(false)
  const [scrubbing, setScrubbing] = useState(false)
  const [hasError, setHasError] = useState(false)

  useEffect(() => {
    if (defaultProgress) {
      ref?.current?.seekTo(defaultProgress.playedSeconds, 'seconds', true)
      setTimeout(() => {
        setPlaying(false)
      }, 100)
    }
  }, [])

  const { playedSeconds, loaded, played } = progress
  // const cur = Duration.fromObject({ seconds: playedSeconds }).toFormat('m:ss')
  // const dur = Duration.fromObject({ seconds: duration }).toFormat('m:ss')

  useEffect(() => {
    setPlaying(ref?.current?.props.playing)
  }, [ref?.current?.props.playing])

  const takeScreenshot = async () => {
    const video = document.getElementsByTagName('video')[0]
    if (video) {
      const canvas = document.createElement('canvas')
      canvas.width = 552
      canvas.height = 310.5
      const ctx = canvas.getContext('2d')
      ctx?.drawImage(video, 0, 0, canvas.width, canvas.height)
      ctx?.restore()
      canvas.toBlob(async (blob: Partial<BlobProps> | null) => {
        if (blob) {
          const blobUrl = URL.createObjectURL(blob as Blob)
          const response = await fetch(blobUrl)
          const data = await response.blob()
          const file = new File([data], 'test', { type: 'image/jpeg' })
          // @ts-ignore
          onScreenshot(file)
        }
      })
    }
  }

  const scrub = (e: any) => {
    // Start scrub - visually update slider, but wait to seek
    setScrubbing(true)
    setPos(e.target.value)
  }

  const onScrub = (e: any) => {
    // End scrub - on mouse up slider - seek to position (sec)
    setScrubbing(false)
    seekTo(pos)
  }

  const seekTo = (seconds) => {
    setHasError(false)
    ref?.current?.seekTo(seconds)
  }

  const toggleVolume = () => {
    setMuted(!muted)
  }

  const onPlay = () => {
    setHasError(false)
    setPlaying(true)
  }

  const onPause = () => {
    setPlaying(false)
  }

  const onDuration = (sec) => {
    setDuration(sec)
  }

  const handleProgress = (state) => {
    setHasError(false)
    setProgress(state)
    if (Boolean(onProgress)) {
      onProgress(state)
    }
  }

  const onBuffer = () => {
    setBuffering(true)
  }

  const onBufferEnd = () => {
    setBuffering(false)
  }

  const handleError = () => {
    onError().then(() => {
      setHasError(false)
      // Seek to where the error occurred
      // Start video again if video was already playing before error
      seekTo(playedSeconds)
    })
  }

  const playerContainerStyle =
    variant === 'video'
      ? {
          // zIndex: 9998,
          position: 'relative',
          paddingTop: '56.25%',
          ...playerContainerStyleOverride,
        }
      : { height: '100%', ...audioContainerStyleOverride }

  const mediaPlayerStyle =
    variant === 'video' ? { position: 'absolute', top: 0, left: 0 } : { marginBottom: 12 }

  return (
    <div style={containerStyle}>
      {hasError ? (
        <Paper style={playerContainerStyle as React.CSSProperties}>
          <div
            style={
              {
                ...mediaPlayerStyle,
                width: '100%',
                height: variant === 'video' ? '100%' : 40,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: 40,
                backgroundColor: color.black,
              } as React.CSSProperties
            }
          >
            <Stack direction="column" gap={2} alignItems="center">
              <Warning fontSize="large" htmlColor={color.white} />
              <Text textAlign="center" sx={{ color: color.white }}>
                The video could not load. Try refreshing the page and loading again. If this still
                does not work, please contact your administrator for assistance.
              </Text>
            </Stack>
          </div>
        </Paper>
      ) : (
        <>
          <div id="media-player" style={playerContainerStyle as React.CSSProperties}>
            <ReactPlayer
              config={{
                file: {
                  forceAudio: variant === 'audio',
                  attributes: {
                    controlsList: 'nodownload',
                    crossOrigin: 'anonymous',
                    id: 'media-player',
                  },
                },
                youtube: {
                  playerVars: { playsinline: true },
                },
                vimeo: {
                  playerOptions: {
                    dnt: true,
                    playsinline: true,
                    vimeo_logo: false,
                  },
                },
              }}
              playsinline={true}
              controls={!showCustomControls}
              onDuration={onDuration}
              pip={false}
              playing={playing}
              light={
                defaultProgress?.playedSeconds || variant === 'audio' ? false : thumbnail || light
              }
              onBuffer={onBuffer}
              onSeek={(seconds) => {
                // console.log(seconds)
              }}
              onBufferEnd={onBufferEnd}
              volume={volume}
              onPlay={onPlay}
              onProgress={handleProgress}
              ref={ref}
              muted={muted}
              onPause={onPause}
              style={mediaPlayerStyle as React.CSSProperties}
              width="100%"
              height={variant === 'video' ? '100%' : 40}
              onError={(err, data) => {
                setHasError(true)
              }}
              url={url}
              progressInterval={50}
              onReady={(p) => {
                setReady(true)
              }}
            />
          </div>
          {ready && defaultProgress?.playedSeconds && Boolean(onScreenshot) ? (
            <Button
              disabled={hasError}
              sx={{ mt: 1 }}
              startIcon={<InsertPhoto />}
              text="Screenshot"
              onClick={takeScreenshot}
            />
          ) : null}
        </>
      )}
    </div>
  )
})
